import * as React from 'react';
import { cva } from 'class-variance-authority';
import PropTypes from 'prop-types';

import { cn } from '@/lib/utils';

const alertVariants = cva(
  'relative w-full rounded-lg border px-4 py-3 text-sm grid has-[>svg]:grid-cols-[calc(var(--spacing)*4)_1fr] grid-cols-[0_1fr] has-[>svg]:gap-x-3 gap-y-0.5 items-start [&>svg]:size-4 [&>svg]:translate-y-0.5 [&>svg]:text-current',
  {
    variants: {
      variant: {
        default: 'bg-sky-100 border-sky-200 text-sky-700 [&>svg]:text-sky-700',
        primary: 'bg-sky-100 border-sky-200 text-sky-700 [&>svg]:text-sky-700',
        destructive: 'bg-rose-100 border-rose-200 text-rose-700 [&>svg]:text-rose-600',
        success: 'bg-emerald-100 border-emerald-200 text-emerald-800 [&>svg]:text-emerald-600',
        warning: 'bg-orange-100 border-orange-200 text-orange-700 [&>svg]:text-orange-600',
        neutral: 'bg-gray-100 border-gray-200 text-gray-700 [&>svg]:text-gray-600',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

const Alert = React.forwardRef(({ className, variant, ...props }, ref) => (
  <div
    ref={ref}
    data-slot="alert"
    role="alert"
    className={cn(alertVariants({ variant }), className)}
    {...props}
  />
));
Alert.displayName = 'Alert';
Alert.propTypes = {
  className: PropTypes.string,
  variant: PropTypes.oneOf(['default', 'primary', 'destructive', 'success', 'warning', 'neutral']),
};

const AlertTitle = React.forwardRef(({ className, children, ...props }, ref) => (
  <div
    ref={ref}
    data-slot="alert-title"
    className={cn('col-start-2 line-clamp-1 min-h-4 font-medium tracking-tight', className)}
    {...props}
  >
    {children}
  </div>
));
AlertTitle.displayName = 'AlertTitle';
AlertTitle.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
};

const AlertDescription = React.forwardRef(({ className, children, ...props }, ref) => (
  <div
    ref={ref}
    data-slot="alert-description"
    className={cn(
      'text-muted-foreground col-start-2 grid justify-items-start gap-1 text-sm [&_p]:leading-relaxed',
      className
    )}
    {...props}
  >
    {children}
  </div>
));
AlertDescription.displayName = 'AlertDescription';
AlertDescription.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
};

export { Alert, AlertTitle, AlertDescription }
